export default {
  'practice-defining-problem-2': {
    'course-title': 'Defining More Complex Problems',
    'course-name': 'Section 5C <span class="unit-title">Defining More Complex Problems<span>',
    'course-video': {
      'title': 'Section 5 Defining More Complex Problems<br/>1’58:28 – 2’05:31 5B VISUALIZING THE PROBLEM :<br/>- ex 5B laying out the parts of the problem',
      'src': 'https://www.youtube.com/embed/5aov7rXM3Vw?enablejsapi=1',
      'lecture-title': '5C Lecture'
    },
    'unit-section-title': '5C Exercises',
    'intro': `<p>Here are three exercises involving more complex, real-world problems on which to practice your newly acquired skill in filling in the Problem Definition Framework. Again, in each case, I give you the facts relating to the problem and three of the five elements of the definition. I then ask you to identify the remaining elements by re-reading the facts supplied. To assist you, I have color-coded the relevant information, which you can find by hovering your mouse over the texts. Once, identified, your job is to place them properly in the framework, and then turn them into an SCQ introduction to match the related pyramid.</p>
<br/>`,
    'menu-list': {
      'exercise-1': 'Location-Based Learning Strategy',
      'exercise-2': 'Group Health Care Plans',
      'exercise-3': 'Houston SEI&M - ISG',
      'exercise-4': 'Spirit Business Systems',
    },
    'exercise-1': {
      'title': '5C - Exercise 1 <br/>Location-Based Learning Strategy',
      'text-1': `<p>Turner Industries, Inc. is a large, diversified, very successful mass-market media corporation catering to children of all ages, and famous for its chain of Kids Place stores. It has lately had its eye on the Location-Based Learning industry, where the use of standardized tests in schools has led to a growing trend for parents to seek private-pay educational services. Currently their only location-based learning alternatives are:</p>
                 <br/>
                 <ul>
                    <li>Remediation Learning Centers, which provide private-paid remediation/enrichment offerings</li>
                    <li>Tutoring Centers, which provide self-directed educational instruction and homework help</li>
                 </ul>
                 <br/>
                 <p>You have been asked to research Location-Based Learning market and determine whether a “Turner Learning Studios” could profitably enter the market.  Following are the notes from your research, and your recommended strategy.</p>
      `,
      'blue-paper-title': `KEY FINDINGS IN LOCATION-BASED<br>LEARNING CENTER MARKET`,
      'blue-paper': `<p><b>Current offerings represent a $12million market</b></p>
      <ul>
        <li>Remediation Learning Centers, which provide private-pay remediation/enrichment offerings ($2 million)</li>
        <li>Tutoring Centers, which provide self-directed educational instruction and homework help ($10 million)</li>
      </ul>
      <p>They address the traditional Turner customer: Ages 5-17, grades K-12.</p>
      <ul>
        <li>Remediation segment dominated by 4-5 national brands</li>
        <li>Tutoring segment is highly fragmented, no dominant player</li>
      </ul>
      <p>Each market presents a challenge to entry.</p>
      <ol>
        <li>Remediation segment is only $2m, unattractive to operate in
            <ul style="list-style-type: disc">
                <li>Kids don’t like it</li>
                <li>Requires state/regional compliance</li>
                <li>Is becoming franchise-based</li>
                <li>Has limited scalability</li>
            </ul>
        </li>
        <li>Tutoring segmentis $10b in size
            <ul style="list-style-type: disc">
                <li>But it is 90% fragmented, with no dominant player</li>
                <li>Is very narrowly focused</li>
            </ul>
        </li>
      </ol>
      <p>There is a clear offering gap in the enrichment segment of the K-5 space, and in programs focusing on Arts and Sciences.</p>
      <div style="display: flex;">
        <p style="margin: 0 5px;">-</p>
        <p>To take advantage of the gaps, Turner would have to differentiate its offering from other traditional Remediation Learning Centers, and find a way to celebrate learning and exploration.</p>
      </div>`,
      'text-3': 'Please drag and drop the elements of the problem definition into their correct positions in the framework',
      'proposed-strategy-title': 'LEARNING CENTER STUDIOS PROPOSED STRATEGY',
      'proposed-strategy': `
        <p>Turner location-based learning (“Turner Learning Studios”) will offer a new and innovative hybrid, fee-based model through the combination of tightly controlled <b>“brick and mortar” centers</b> and an <b>online enrichment curriculum.</b></p>
        <ul>
          <li>
            Students can enroll in the Turner Learning Studios for a monthly fee, which includes unlimited visits to the center and access to the online enrichment curriculum
          </li>
          <li>
            TLS will provide a unique experience for students to experience <b>hands-on enrichment</b> models in Arts and Science, directed by Center facilitators
            <ul>
              <li>Explore a store showcase, and launch 3-5 Turner Learning Studios in key metropolitan areas</li>
              <li>Create an explorative atmosphere, similar to a science museum, whereby students can engage with various stations</li>
            </ul>
          </li>
          <li>
            TLS will offer continuation of the center-based experience through an extended <b>online enrichment</b> curriculum
            <ul>
              <li>The monthly fee will permit access to Turner learning curriculum online</li>
              <li>Also to an independent  extension of the learning enrichment inside the centers</li>
            </ul>
          </li>
          <li>
            TLS should use the existing Kids Place stores as the locations
            <ul>
              <li>The stores are an ideal marketing platform</li>
              <li>Flagship stores ideal for offering learning products
                <ul>
                    <li>Will convert a small portion into learning pods that enable sampling</li>
                    <li>Will market directly to kids K-5 who are TLS customers</li>
                </ul>
              </li>
            </ul>
          </li>
          <li>
            TLS marketing presence will help strengthen overall Turner brand
          </li>
        </ul>`,
      'intro-1': `<h3 class="intro__title">Full wording of the introduction</h3>
    <br/>
    <br/>
    <div class="intro__introduction">
            <p>As part of our growth strategy, we have been looking closely at the Location-Based Learning market. Greater use of standardized tests has led a growing number of parents to seek private-pay educational services for their children. Generally of two types: Remediation Learning Centers ($2m) that provide private-pay remediation/enrichment offerings, and Tutoring Centers ($10m) that provide self-directed educational instruction and homework help. Addresses same market Turner does: ages 5-17, grades K- 5.</p><br/>
            <p>Neither segment individually is very attractive, but together they do offer a $12m market that addresses the same market Turner does (ages 5-17, grades K-5). And neither has a dominant player. Research indicates, further, that there is a clear gap in the offerings of both segments that Turner might fill, if it made commercial sense to do so.</p><br/>
    <p>Further analysis indicates that it could make commercial sense. Turner would have to differentiate its offering from those of the traditional Remediation Learning Center, and extend it to the Turner learning curriculum online.</p>
    </div>`,
    },
    'exercise-2': {
      'title': '5C - Exercise 2<br/>Cascade Group Health Care Plans',
      'text-1': `<p>Assume you are a partner in Eastwood Consulting firm, and have just finished discussing a possible consulting engagement with a new client, Cascade Group Health Care Plans. You have jotted the following notes to yourself, and need to put together a formal proposal to them. But first you need to define their problem more succinctly, so that you can determine your approach to it.</p>`,
      'text-2': `<strong>Here is the structure of the document you will write. Now let’s lay out the problem that will be the basis of your introduction.</strong>
      <br/><br/>
      <p class="structure"><img src="/assets/images/structure-5b-3.png" alt="Original structure"></p>`,
      'blue-block': `<h3>NOTE ON NEW ENGAGEMENT</h3>
<br/>
<p><u>The Client</u></p>
<br/>
<p>
This engagement will be conducted on behalf of Cascade Group Health Care Plans, covering the health care plans of five states:
<ul style="list-style: none; padding-left: 20px">
    <li>Health Care of Arizona</li>
    <li>Health Care of California</li>
    <li>Health Care of Iowa and South Dakota</li>
    <li>Health Care of Massachusetts</li>
</ul>
The Cascade Group hopes that Eastwood can design a core information system based on the Groups’ common business needs, and also identify opportunities for collaboration and migration options.
</p>
<br/>
<p><u>The Objectives</u></p>
<br/>
<p>
The stated objectives of this initiative are
<ul style="padding-left: 20px">
    <li>To help the participating Plans develop a shared vision for future core systems
	that meets common future business requirements</li>
    <li>To assess options for transformation of the current systems environment</li>
    <li>To identify opportunities to collaborate on various initiatives that help achieve the vision</li>
</ul>
</p>

<p>
Within these overall objectives, Eastwood has said we would address a series of “specific issues”:
<ul style="padding-left: 20px">
    <li>Identify commonalities among the Plans in terms of business direction,
	customer needs, products and services</li>
    <li>Identify common business processes that require IT support</li>
    <li>Identify common IT functionality requirements</li>
    <li>Describe the applications required to provide this functionality</li>
    <li>Perform a gap analysis</li>
    <li>Identify what can be supported by current systems</li>
    <li>Identify what must be acquired or built</li>
    <li>Describe and assess options that close the gap</li>
</ul>
</p>
<p><u>Going-in Facts</u></p>
<ol style="padding-left: 0px">
    <li><b>Business Direction.</b> Each Plan’s market characteristics and strategies differ, but
	  share common elements.</li>
    <li><b>Market characteristics.</b> The healthcare market is rapidly changing. The role played by the Cascade Group is moving beyond the traditional insurer role to that
	  of managing medical costs, delivering care by owning provider organisations,
	  and developing, packaging, marketing, and selling “healthcare products.”</li>
    <li><b>Information technology challenges.</b> Plans complain about system
	  inflexibility, lack of easy access to useful information across the extended
	  business environment, high level of complexity of current legacy-based systems
	  and proprietary languages, and the high cost of operating in the current IT
	  environment. They need a fundamentally new architecture that is flexible, open,
	  user-friendly, modular, and accessible to users across the extended enterprise.</li>
    <li><b>Architecture migration.</b> Need to migrate from the monolithic, legacy systems
	  and architectures to flexible, open, modular systems and a communication
	  environment that supports an extended, virtual enterprise.</li>
</ol>`,
      'intro-2': `<h3 class="intro__title">Full wording of the introduction</h3>
    <br/>
    <div class="intro__introduction">
    <p>Cascade Group’s offices across the country are equipped with IT systems chosen and installed individually by each office. Over the last 10-15 years each office has complained that the systems are inflexible in structure and complex to operate. They are also very high cost compared to more modern vintages, and provide little relevant market information.</p>
<br/>
<p>This problem has become serious, given continuing changes in the health care market and the Plans’ needs to respond to the differing demands of their local state customers. Research into the cost of replacing all four of the systems indicates the cost would be unacceptably high.</p>
<br/>
<p>However, the plans do have many commonalities in terms of processes and functionalities, even though individual needs differ widely in other ways. The most economical approach would be for Cascade to develop a single core system for the Group and adapt it to meet the local needs of each market. Cascade has asked Eastwood to determine whether a single system is possible, and if so what it should look like and how it could be implemented.</p>
<br/>
<p>(Can Eastwood develop a single system to be used economically all four Cascade offices?)</p>
</div>`,
    },
    'exercise-3': {
      'title': '5C - Exercise 3<br/>Houston’s Information Systems Group',
      'text-1': `
      <div class="desc-scq-diagram">
            <div class="desc-scq-diagram__title">
                <p>HOUSTON SEI&M - ISG</p>
            </div>
            <div class="scq-text">
                <div class="scq-block">
                    <p>S = Planning to enter external market</p>
                </div>
                <div class="scq-block">
                    <p>C = Tough to be successful without strategy</p>
                </div>
                <div class="scq-block">
                    <p>Q = What should strategy be?</p>
                </div>
            </div>
            <div class="scq-diagram">
                <div class="scq-block">
                    <p>ISG’s strategy should be to move in slowly, and expand as its strengths grow</p>
                </div>
                <svg>
                    <line x1="450" y1="0" x2="85" y2="100"/>
                    <line x1="450" y1="0" x2="325" y2="100"/>
                    <line x1="450" y1="0" x2="575" y2="100"/>
                    <line x1="450" y1="0" x2="810" y2="100"/>
                </svg>
                <div class="scq-diagram-body">
                <div class="scq-block">
                    <p>Start as niche provider of systems, management & maintenance in non-DOD markets</p>
                </div>
                <div class="scq-block">
                    <p>Enter via an enabling acquisition </p>
                </div>
                <div class="scq-block">
                    <p>Develop a strong market-oriented organization</p>
                </div>
                <div class="scq-block">
                    <p>Migrate from niche provider to broader market coverage</p>
                </div>
                </div>
            </div>
        </div>
        <br/>
        <p>But first you have to structure the introduction to the strategy plan the consultants will send to ISG. Here is the information you have available to you.</p>
        <br/>
        <p style="font-size: 25px"><b>WHAT YOU KNOW</b></p>
        <br/>
        <div style="border: 2px solid #000000">
            <ol>
                <li style="margin-bottom: 15px;">The external SEI&M market is $3.5-5 billion, growing 10-15% a year.  Two segments:  federal and commercial
                    <ul style="list-style-type: disc">
                        <li>Federal is 70% Department of Defense</li>
                        <li>Commercial is full of small contractors</li>
                    </ul>
                </li>
                <li style="margin-bottom: 15px;">It is a very tough market
                    <ul style="list-style-type: disc">
                        <li>Highly competitive</li>
                        <li>Providers tend to build on their strengths, often compete in more than one area</li>
                        <li>Key success factors differ for each segment, many niches within segments</li>
                    </ul>
                </li>
                <li>ISG wants a workable strategy that will enable it to be successful</li>
            </ol>
        </div>
`,
      'blue-paper-title': `HOUSTON SEI&M - ISG`,
      'blue-paper': `<p>SG is the internal Information Systems Group of Houston SEI&M, a large corporation operating in the Systems Engineering, Integration, and Management market.  It provides Houston with professional services and integrated office automation systems covering:</p>
      <ul>
        <li>Needs analysis</li>
        <li>Systems design and development</li>
        <li>Installation and integration</li>
        <li>Systems management and maintenance</li>
      </ul>
      <p>In addition to the team’s technical expertise in these areas, it has good project management skills.</p>
      <br/>
      <p>Houston’s need for these services is slowing, and the team is in danger of being broken up.  Accordingly, it is looking to offer its highly honed services externally, to the market at large.  All it needs is the right strategy in what is really a tough market with myriad ways to set up shop.</p>
      <br/>
      <p>Imagine that you head the consulting team that has been called in to work with ISG.  You have concluded IS Group has a good idea here, and have determined what their strategy should be.  You are ready to write to recommend it.  You know you want to structure the document around the steps in the strategy, as shown on below.</p>
      `,
      'intro-3': `<h3 class="intro__title">Full wording of the introduction</h3>
    <br/>
    <br/>
    <div class="intro__introduction">
            <p>Information Systems Group is planning to leave Houston and enter the external Systems Engineering and Management market. Having honed your skills over the last 20 years, and understanding the demands of the market, you are in an excellent position to compete with current incumbents in providing expert systems design and development. Nevertheless, making a success will not come easily.</p>
            <br>
            <p>The market itself is dominated by the Department of Defense, which does favor dealing with local suppliers. And while most business is conducted through purchase orders, DOD imposes stringent rules for submitting those proposals. A thorough understanding of these processes is essential. The rest of the market is also highly competitive, consisting of a variety of segments catering to specialized niches with widely differing success factors. The key to success is identifying the right niche for ISG’s particular strengths. To that end, the Group needs a workable strategy.</p>
            <br>
            <p>(What strategy do you recommend?)</p>
    </div>`,
    },
    'exercise-4': {
      'title': '5C - Exercise 4<br/>Spirit Business Systems',
      'text-1': `<p>Following is the Executive Summary of a proposal to Spirit Business Systems plus the additional facts you would need to know if you were writing the proposal from scratch. It is structured in the classic consulting firm way, but with an effort to give a sense of the point each section is trying to make:</p>
      <br/>
      <ul>
        <li>Our Proposition</li>
        <li>Our Qualifications – World Class Capabilities</li>
        <li>The Process – Seamless teaming</li>
        <li>The Value to Spirit</li>
      </ul>
      <br/>
      <p>Nevertheless, it does not clearly explain the problem to which Eastwood is offering a solution. And, as it turns out, it is a double-layer problem. But first, here are the facts you need to know about Sprit to be able to understand the proposal.</p>`,
      'blue-paper': `<h5><ins>Facts about Spirit</ins></h5>
        <ul class="spirit-facts-blue-paper">
            <li>The entity we are talking to is Spirit Fulfillment Division
                <p><span class="span-marginX">-</span>It currently has two Systems Development facilities, Dallas and Kansas City</p>
                <p><span class="span-marginX">-</span>The two facilities design new products and services, and maintain the Information Systems for the company as a whole</p></li>
            <li>The SLD work force has grown from 1300 to 1700 people over the last 2 years, mostly using outside contractors
                <p><span class="span-marginX">-</span>Demand for services is projected to reach 1 million hours a year over each of the next 5 years</p>
                <p><span class="span-marginX">-</span>That will require another 500 people a year to meet that demand</p>
                <p><span class="span-marginX">-</span>No way the two facilities can absorb that many people without a major reduction in productivity</p>
            </li>
            <li>SLD wants to handle the growing work load efficiently
                <p><span class="span-marginX">-</span>It needs to build a separate facility to house the new entrants</p>
                <p><span class="span-marginX">-</span>It wants building the facility to cause minimum disruption to day-to-day work</p>
                <p><span class="span-marginX">-</span>It wants the addition to operations of the new plant to be seamless</p>
            </li>
            <li>SLD does not have the resources to build, staff, nor manage a new facility
                <p><span class="span-marginX">-</span>It is looking to hire an outside company to build, staff, and manage the project</p>
            </li>
        </ul>
    `,
      'text-2': `"And here are the first two sections of the proposal that was written based on these facts. You can see that it is somewhat awkwardly structured around headings rather than around the approach the consultant will take to solve the problem, with "Our Qualifications" coming before the explanation of the work<br/><br/>
                 Tsk Tsk.<br/><br/>`,
      'blue-paper-additional-header': '<h5 class="blue-paper-2-header"><b>What You Know</b></h5>',
      'blue-paper-2': `
        <div class="blue-paper-header" style="top: 114px;">
            <div>
            <p style="margin-left: 24px;">Spirit Business Systems</p>
            <p id="date-to-change" style="margin-left: 65px;"></p>
            </div>
            <div style="margin-top: 19px;">
            <p style="margin-left: 47px;">Eastwood Consulting</p>
            <p style="margin-left: 86px;">Executive summary</p>
            </div>
        </div>
<!--        <p><span class="blue-paper-2__summary">Executive Summary</span></p>-->
        <div>
<!--            <br/>-->
            <p>Eastwood is pleased to have this opportunity to architect, build, manage and operate a world-class “Business Systems Development Center” for Spirit. The “Spirit Solutions Center” we propose will provide Spirit with the systems development expertise, enabling technologies, telephone industry knowledge objects, technical resources and business management expertise that are crucial to the rapid development and implementation of critical, new business applications.</p>
            <br/>
            <p>As Spirit’s leading professional services firm, we fully appreciate and applaud the emphasis Spirit is placing on this challenge. And together with our alliance partners, we are uniquely positioned to provide the resources, personnel and expertise needed to create and operate a world-class development center – a center that will dramatically propel Spirit forward and allow it to achieve both its short and its long-term strategic and operational business objectives.</p>
            <br/>
            <p>Eastwood is confident that we can make the Spirit Solutions Center the benchmark against which comparable centers will be measured. Our confidence stems from the caliber of the team we are proposing – a team that has been responsible for successfully building and operating similar world class centers for ourselves and for our clients. As such, we bring with us the deep knowledge that results from the fusion of best practices and hands-on experience. We know that we can immediately add value to Spirit – yielding superior speed and productivity – at a fraction of the risk normally associated with such an undertaking.</p>
            <br/>
            <p>Our proposition is as follows:</p>
        </div>
        <br/>
        <p><span class="blue-paper-2__summary">Our Proposition – The Spirit Solutions Center</span></p>
        <div>
            <br/>
            <p>Working together with Spirit, the Team will architect, build, staff and operate a “world class” business systems solution center for Spirit. The Spirit Solutions Center will provide Spirit with the superior capabilities required to conceptualize, design, program and implement advanced business system applications, as well as enhance legacy systems. The Spirit Solutions Center will have the capacity to generate one million hours of annual systems development work.</p>
        </div>
        <br/>
        <p style="position: relative; z-index: 3;"><span class="blue-paper-2__summary">Our Qualifications – World-Class Capabilities</span></p>`,
      'text-3': `
      <div class="desc-scq-diagram">
            <div style="max-width: 340px" class="desc-scq-diagram__title">
                <p>SUGGESTED STRUCTURE<br/>PROPOSAL TO SPIRIT</p>
            </div>
            <div class="scq-text">
                <div class="scq-block">
                    <p>S = </p>
                </div>
                <div class="scq-block">
                    <p>C = Want up and running in 6 months with minimum disruption to existing operations</p>
                </div>
                <div class="scq-block">
                    <p>Q = (Can you help us to do that?)</p>
                </div>
            </div>
            <div class="scq-diagram">
                <div style="max-width: 250px;" class="scq-block">
                    <p>Eastwood has the qualities neces-sary to deliver and manage an outstanding new systems development facility for Spirit </p>
                </div>
                <svg>
                    <line x1="450" y1="0" x2="85" y2="100"/>
                    <line x1="450" y1="0" x2="450" y2="100"/>
                    <line x1="450" y1="0" x2="805" y2="100"/>
                </svg>
                <div class="scq-diagram-body">
                <div class="scq-block">
                    <p>We have a clear vision of the finished facility that Spirit needs</p>
                </div>
                <div class="scq-block">
                    <p>We will bring together the necessary levels of expertise to get the job done</p>
                </div>
                <div class="scq-block">
                    <p>We can move expeditiously to bring the whole project to life</p>
                </div>
                </div>


            </div>
        </div>`,
    },
    'intro-4': `
        <p>Spririt’s Systems Development Center has grown over the last 2 years, from 1300 to 1700 people. Spirit is planning to grow even more to meet a projected demand of 1 million hours a year for each of the next 5 years. Meeting that demand will require adding another 500 people. Neither Dallas nor Kansas City facility can absorb that many new hires and still be productive.</p>
        <p>Consequently, Spirit has decided to build a separate unit to house the new entrants.  It wants to make certain that the new unit will be up and running efficiently within 6 months, with minimum disruption to existing operations.  Spirt has decided to hire an outside company to build, staff, and manage the facility.</p>
        <p>(Are you the one to help us do that?)</p>`,
    'wording-introduction-paper': {
      'desc': 'Now that you know the essence of what your proposal will say, and given the examples you have seen of good introductions, why not try to write out the full introduction yourself this time. You can then compare it to my version.',
      'revealIntroduction': 'VIEW SCHOOL SOLUTION',
      'header': 'Eastwood consulting and the company in this exercise',
    },
    'congratulation-message': 'CONGRATULATIONS',
    'finish-section': 'You have completed Section 5C',
    'final-description': 'You are fully able both to structure the definition of a problem<br/> and to convert it into a proper SCQ introduction',
    'next-course-button': 'proceed to section 5D',
    'dashboard-button': 'Main menu',
  }
}



// WEBPACK FOOTER //
// ./client/app/modules/courses/5/5c/practiceDefiningProblem2/i18n/en.js